// UserInfoForm.js
import React, { useState, useCallback, useEffect } from "react";
import {
  TextField,
  Button,
  Box,
  Typography,
  InputAdornment,
  IconButton,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import SavedSearchIcon from "@mui/icons-material/SavedSearch";
import ArticleIcon from "@mui/icons-material/Article";
import WorkHistoryIcon from "@mui/icons-material/WorkHistory";
import PublishedWithChangesIcon from "@mui/icons-material/PublishedWithChanges";
import StarIcon from "@mui/icons-material/Star";
import { validateEmail, debounce } from "../utils/validation";
import "./UserInfoForm.css";

const benefits = [
  {
    icon: "ai-insights.svg",
    text: "AI-powered career insights",
  },
  {
    icon: "career-avatar.svg",
    text: "Find your Career Avatar insights",
  },
  {
    icon: <SavedSearchIcon />,
    text: "Boost job search with tools",
  },
  {
    icon: <ArticleIcon />,
    text: "Access resume samples for reference",
  },

  {
    icon: <WorkHistoryIcon />,
    text: "Get objective career strengths insights",
  },
  {
    icon: <PublishedWithChangesIcon />,
    text: "Learn job-hunting and career switching",
  },
];

const UserInfoForm = ({
  userInfo,
  setUserInfo,
  handleSendOtp,
  handleVerifyOtp,
  otpSent,
  otpVerified,
  otp,
  setOtp,
  errorMessage,
  onClose,
  submitting,
}) => {
  const [showConfetti, setShowConfetti] = useState(false);
  const [otpTimer, setOtpTimer] = useState(300);
  const [emailValidation, setEmailValidation] = useState({
    isValid: true,
    error: null,
  });
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    let interval;
    if (otpSent && !otpVerified && otpTimer > 0) {
      interval = setInterval(() => {
        setOtpTimer((prevTimer) => prevTimer - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [otpSent, otpVerified, otpTimer]);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  const debouncedValidateEmail = useCallback(
    debounce((email) => {
      const result = validateEmail(email);
      setEmailValidation(result);
      if (result.isValid && result.normalizedEmail !== email) {
        setUserInfo((prev) => ({ ...prev, email: result.normalizedEmail }));
      }
    }, 300),
    []
  );

  const handleEmailChange = (e) => {
    const email = e.target.value;
    setUserInfo((prev) => ({ ...prev, email }));
    setIsDirty(true);
    debouncedValidateEmail(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!otpSent) {
      const validation = validateEmail(userInfo.email);
      if (!validation.isValid) {
        setEmailValidation(validation);
        return;
      }
      await handleSendOtp();
    } else if (!otpVerified) {
      const success = await handleVerifyOtp();
      if (success) {
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 5000);
      }
    }
  };

  return (
    <Box className="user-info-form-container">
      <Box className="user-info-form-wrapper">
        <Box className="benefits-section">
          <div className="overlay-container">
            <div className="overlay-images">
              <img
                src="/assets/icons/top-image-combined.svg"
                alt=""
                className="overlay-image"
              />
            </div>
          </div>
          <div className="benefits-content">
            <Typography
              className="benefits-title"
              sx={{
                fontSize: "2em",
                color: "#fff",
                marginBottom: "2.5rem",
                lineHeight: 1.2,
                "&.benefits-title": {
                  fontSize: "2em",
                  color: "#fff",
                  marginBottom: "2.5rem",
                  lineHeight: 1.2,
                },
              }}
            ></Typography>
            <div className="benefits-list">
              {benefits.map((benefit, index) => (
                <div key={index} className="benefit-item">
                  {typeof benefit.icon === "string" ? (
                    <img
                      src={`/assets/icons/${benefit.icon}`}
                      alt=""
                      className="benefit-icon"
                    />
                  ) : (
                    <span className="benefit-icon">{benefit.icon}</span>
                  )}
                  <span className="benefit-text">{benefit.text}</span>
                </div>
              ))}
            </div>
          </div>
          {/* <div className="premium-badge">
            <StarIcon sx={{ fontSize: 14 }} />
            <span>-</span>
            <span>Premium Access Benefits</span>
          </div> */}
        </Box>

        <Box className="form-section">
          <Typography
            className="form-title"
            sx={{
              fontSize: "1.5rem",
              fontWeight: 600,
              color: "#1a1a1a",
              marginBottom: "1rem",
              textAlign: "center",
              "&.form-title": {
                fontSize: "1.5rem",
                fontWeight: 600,
                color: "#1a1a1a",
                marginBottom: "1rem",
                textAlign: "center",
              },
            }}
          >
            Enter your details to unlock Career Insights
          </Typography>
          <Typography
            className="form-subtitle"
            sx={{
              fontSize: "1rem",
              color: "#666",
              marginBottom: "2rem",
              textAlign: "center",
              "&.form-subtitle": {
                fontSize: "1rem",
                color: "#666",
                marginBottom: "2rem",
                textAlign: "center",
              },
            }}
          ></Typography>

          <form onSubmit={handleSubmit}>
            <TextField
              className="form-input"
              label="Name"
              variant="outlined"
              value={userInfo.name}
              onChange={(e) =>
                setUserInfo({ ...userInfo, name: e.target.value })
              }
              required
              fullWidth
              disabled={otpSent}
              placeholder="Name *"
            />
            <TextField
              className="form-input"
              label="Email"
              variant="outlined"
              value={userInfo.email}
              onChange={handleEmailChange}
              onBlur={() => {
                const validation = validateEmail(userInfo.email);
                setEmailValidation(validation);
                if (validation.isValid) {
                  setUserInfo((prev) => ({
                    ...prev,
                    email: validation.normalizedEmail,
                  }));
                }
              }}
              error={isDirty && !emailValidation.isValid}
              helperText={isDirty && emailValidation.error}
              required
              fullWidth
              disabled={otpSent}
              placeholder="Email *"
              InputProps={{
                endAdornment: isDirty && (
                  <InputAdornment position="end">
                    {emailValidation.isValid ? (
                      <CheckCircleIcon color="success" />
                    ) : (
                      <ErrorIcon color="error" />
                    )}
                  </InputAdornment>
                ),
              }}
            />
            {otpSent && !otpVerified && (
              <TextField
                className="form-input"
                label="OTP"
                variant="outlined"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                required
                fullWidth
                inputProps={{
                  maxLength: 6,
                  pattern: "[0-9]*",
                }}
                helperText={`OTP valid for: ${formatTime(otpTimer)}`}
              />
            )}
            {errorMessage && (
              <Typography color="error" sx={{ mb: 2 }}>
                {errorMessage}
              </Typography>
            )}
            <Button
              className="form-button"
              type="submit"
              variant="contained"
              fullWidth
              disabled={
                submitting ||
                (otpSent && otpTimer === 0) ||
                (!otpSent &&
                  (!emailValidation.isValid ||
                    !userInfo.email ||
                    !userInfo.name))
              }
              sx={{
                backgroundColor: "#000000",
                color: "#ffffff",
                "&:hover": {
                  backgroundColor: "#333333",
                },
                "&.Mui-disabled": {
                  backgroundColor: "#cccccc",
                  color: "#666666",
                },
              }}
            >
              {submitting
                ? "Processing..."
                : otpSent
                ? "Verify Email OTP"
                : "Verify Email"}
            </Button>
          </form>
        </Box>
      </Box>
    </Box>
  );
};

export default UserInfoForm;
